import { FC, MouseEvent } from 'react';
import classNames from 'classnames';
import { DocumentTextIcon, TrashIcon } from '@heroicons/react/outline';

import {
  MailSequenceScheduledSendStatus,
  MailSequenceSkipReason,
} from '../../../../generated/graphql';

import ActionButton from '../../../components/ActionButton';
import ActionButtonGroup from '../../../components/ActionButtonGroup';
import { TotalAttachmentsCountBadge } from '../../../components/ActivityAttachmentBadges';
import {
  SCHEDULED_SEND_STATUS_LABEL,
  SKIP_REASON_LABEL,
} from '../../../lib/mailSequence';

/** Match Activities tab status pills: colored bg + neutral text. */
const STATUS_PILL: Record<MailSequenceScheduledSendStatus, string> = {
  [MailSequenceScheduledSendStatus.Pending]: 'bg-blue-25',
  [MailSequenceScheduledSendStatus.Sending]: 'bg-blue-25',
  [MailSequenceScheduledSendStatus.Sent]: 'bg-green-25',
  [MailSequenceScheduledSendStatus.Failed]: 'bg-red-25',
  [MailSequenceScheduledSendStatus.Skipped]: 'bg-neutral-50',
  [MailSequenceScheduledSendStatus.Cancelled]: 'bg-neutral-50',
};

export type ScheduledSendCardModel = {
  stepIndex: number;
  subject: string;
  providerNote: string;
  status: MailSequenceScheduledSendStatus;
  skipReason?: MailSequenceSkipReason | null;
  failureReason?: string | null;
  activityCount: number;
};

interface Props {
  send: ScheduledSendCardModel;
  /** Text after the status pill, e.g. "for Jul 23, 2026" */
  captionSuffix: string;
  captionSecondary?: string;
  canSkip?: boolean;
  onSkip?: () => void;
  onClick?: () => void;
  /** When false, card is not clickable (e.g. schedule preview). */
  interactive?: boolean;
  /** Hide send-status pill (e.g. template / cadence-only previews). */
  hideStatus?: boolean;
}

const ScheduledSendCard: FC<Props> = ({
  send,
  captionSuffix,
  captionSecondary,
  canSkip = false,
  onSkip,
  onClick,
  interactive = true,
  hideStatus = false,
}) => {
  const hasMessage = send.providerNote.trim().length > 0;
  const title = send.subject?.trim() || `Mail ${send.stepIndex + 1}`;

  const handleSkipClick = (e: MouseEvent) => {
    e.stopPropagation();
    onSkip?.();
  };

  const handleActivate = () => {
    if (interactive && onClick) onClick();
  };

  return (
    <div
      className={classNames(
        'relative flex h-full min-h-full w-[320px] shrink-0 snap-start flex-col gap-y-3 self-stretch rounded-lg bg-white p-5 shadow-100',
        interactive &&
          'cursor-pointer hover:bg-neutral-25 focus:outline-none focus:ring-2 focus:ring-secondary-50',
      )}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? handleActivate : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleActivate();
              }
            }
          : undefined
      }
    >
      <div className="flex flex-col">
        <div className="flex items-center justify-between gap-x-2">
          <div className="flex min-w-0 flex-1 items-center gap-x-2">
            <span className="text-sm flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-bold text-white">
              {send.stepIndex + 1}
            </span>
            <div className="text-body-sm min-w-0 flex-1 font-medium leading-tight text-neutral-150 line-clamp-2">
              {title}
            </div>
          </div>
          <div className="flex h-7 w-7 shrink-0 items-center justify-end">
            {canSkip && onSkip ? (
              <ActionButtonGroup size="small">
                <ActionButton
                  size="small"
                  aria-label="Skip mail"
                  title="Skip mail"
                  onClick={handleSkipClick}
                >
                  <TrashIcon className="h-4 w-4 text-red-100" />
                </ActionButton>
              </ActionButtonGroup>
            ) : null}
          </div>
        </div>

        <div className="mt-4 text-neutral-110">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-1 text-caption">
            {!hideStatus ? (
              <span
                className={classNames(
                  'inline-flex shrink-0 items-center justify-center rounded-full px-2 py-0.5 text-caption text-neutral-150',
                  STATUS_PILL[send.status],
                  send.status === MailSequenceScheduledSendStatus.Sending &&
                    'animate-pulse',
                )}
              >
                {SCHEDULED_SEND_STATUS_LABEL[send.status]}
              </span>
            ) : null}
            <span className="min-w-0 font-medium text-neutral-125">
              {captionSuffix}
            </span>
          </div>
          <div className="mt-0.5 min-h-[1.125rem] truncate pl-2 text-small-caption font-light">
            {captionSecondary ?? '\u00a0'}
          </div>
        </div>
      </div>

      <div className="mt-auto flex flex-col gap-y-2 pt-4">
        <div>
          <div className="mb-1.5 text-small-caption font-medium text-neutral-125">
            Includes:
          </div>
          {hasMessage || send.activityCount > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {hasMessage && (
                <div
                  className="flex items-center gap-x-1 rounded-full bg-neutral-50 px-2 py-1"
                  aria-label="Includes a message"
                  title="Includes a message"
                >
                  <DocumentTextIcon className="h-4 w-4 text-neutral-150" />
                  <span className="text-small-caption font-medium text-neutral-150">
                    Message
                  </span>
                </div>
              )}
              <TotalAttachmentsCountBadge count={send.activityCount} />
            </div>
          ) : (
            <p className="text-small-caption italic text-neutral-110">None</p>
          )}
        </div>

        {send.status === MailSequenceScheduledSendStatus.Skipped &&
          send.skipReason && (
            <div className="bg-yellow-25/60 text-yellow-150 rounded-md px-2 py-1 text-small-caption line-clamp-2">
              {SKIP_REASON_LABEL[send.skipReason] ?? send.skipReason}
            </div>
          )}
        {send.status === MailSequenceScheduledSendStatus.Failed &&
          send.failureReason && (
            <div className="rounded-md bg-red-25 px-2 py-1 text-small-caption text-red-150 line-clamp-2">
              {send.failureReason}
            </div>
          )}
      </div>
    </div>
  );
};

export default ScheduledSendCard;
